'use client';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { ReactComponent as SvgLogoSnow } from 'public/assets/white-logo.svg';
import { ReactComponent as SvgLogoSki } from 'public/assets/white-logo_ski.svg';
import { ReactComponent as SvgLogoMountainNewsSummer } from 'public/assets/mountain-news-summer.svg';
import { ReactComponent as SvgLogoMountainGetawaySummer } from 'public/assets/mountain-getaway-summer.svg';
import { ReactComponent as SvgSunWhite } from 'public/assets/icons/sun-white.svg';
import { ChevronDown, Search } from 'lucide-react';

import { cn } from '@/util/cn';
import { Routes } from '@/routes';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SimpleLangSelect } from '@/components/resort/HeaderLangSelect/HeaderLangSelect';
import { useAnalytics } from '@/tracking/analytics';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { isMainLocale } from '@/util/i18nFlags';
import { Container } from '@/components/ui/container';

import { IS_SSR } from '@/ots-constants';
import { setFocusToSearchInput } from '@/util/setFocusToInputByQuery';
import { useNoHeader } from '@/util/useNoHeader';
import { useDefaultScrollToTop } from '@/util/useScrollToTop';

import { useSettingsPrefetch } from '@/settings/SettingsContext';
import { locale } from '@/i18n/constants';
import { getSummerLogoHrefUrl } from '@/util/getSummerLogoHrefUrl';
import { REVIEW_ANCHOR } from '@/page-components/resort/constants';

import { SUMMER_NAV_BAR_URLS } from './summerURLs';

// Search is gated on user action; mirror the lazy-load pattern from
// HeaderNav so the summer header doesn't pull these into the page bundle.
const HeaderSearch = dynamic(
  () => import('@/components/common/HeaderSearch/HeaderSearch').then((m) => m.HeaderSearch),
  { ssr: false, loading: () => null },
);
const SearchPopup = dynamic(
  () => import('@/components/home/SearchPopup').then((m) => m.SearchPopup),
  { ssr: false, loading: () => null },
);

type SummerNavLocales = 'en-US' | 'en-GB' | 'fr-FR' | 'de-DE' | 'it-IT' | 'pl-PL';

const summerNavLocales: SummerNavLocales[] = ['en-US', 'en-GB', 'fr-FR', 'de-DE', 'it-IT', 'pl-PL'];

const isSummerLocale = summerNavLocales.includes(locale as SummerNavLocales);

const summerURLs = (isSummerLocale && SUMMER_NAV_BAR_URLS[locale as SummerNavLocales]) || [];

export enum HeaderNavCurLink {
  SNOW_REPORTS = 'snow_reports',
  TRIP_PLANNING = 'trip_planning',
  MAGAZINE = 'magazine',
  MAIN = 'index',
  SKIGPT = 'skigpt',
}

// Off-season + summer-overview chrome. Mirrors winter HeaderNav at
// the structure level — same v3 bar height, same HeaderSearch on
// desktop, same compact mobile bar with Lucide icons — and just
// swaps the brand palette. The Stories dropdown (summer-only nav
// surface) opens on hover and lists the per-locale magazine URLs.
//
// Palette: defaults to the winter blue chrome (this header also
// shows in shoulder seasons via useShouldAdjustForSummer); flips to
// green when the page is the dedicated summer-overview surface.

const winterPalette = {
  bg: 'bg-primary',
  hoverBg: 'hover:bg-primary-hover',
  currentBg: 'bg-primary-hover',
  subBarBg: 'bg-primary-hover',
  dropdownHover: 'hover:bg-primary-hover',
  mobileActive: 'bg-primary-hover',
};

const summerPalette = {
  bg: 'bg-primary-summer',
  hoverBg: 'hover:bg-primary-summer-hover',
  currentBg: 'bg-primary-summer-hover',
  subBarBg: 'bg-primary-summer-hover',
  dropdownHover: 'hover:bg-primary-summer-hover',
  mobileActive: 'bg-primary-summer-hover',
};

const desktopLinkClass = [
  'flex h-full items-center rounded-sm px-3 text-xs font-medium uppercase tracking-wider leading-none',
  'text-white/90 no-underline transition-colors',
  'hover:text-white',
].join(' ');

const mobileLinkClass = [
  'flex h-10 items-center px-3 text-[0.7rem] font-medium uppercase tracking-wider',
  'text-white no-underline transition-colors',
  'hover:bg-white/10',
].join(' ');

const mobileIconButtonClass = [
  'flex h-10 w-10 cursor-pointer items-center justify-center text-white',
].join(' ');

const subBarLinkClass = [
  'flex items-center gap-1 text-xs font-medium uppercase tracking-wider',
  'text-white/90 no-underline transition-colors',
  'hover:text-white',
].join(' ');

export const SummerHeader = ({
  current,
  isOnSummerOverview,
}: {
  current: HeaderNavCurLink;
  isOnSummerOverview?: boolean;
}) => {
  const prefetch = useSettingsPrefetch();
  const { t } = useOTSTranslation('common');
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
  const a = useAnalytics();

  const palette = isOnSummerOverview ? summerPalette : winterPalette;
  const divRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLAnchorElement>(null);
  const pathname = usePathname() ?? '';
  const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';

  const logoHrefUrl = getSummerLogoHrefUrl(isOnSummerOverview || false);

  const toggleSearch = useCallback(() => {
    new Promise<void>((resolve) => {
      setSearchOpen((v) => !v);
      if (!isSearchOpen) {
        a.searchBox();
      }
      resolve();
    }).then(() => {
      setFocusToSearchInput();
    }).catch(() => {});
  }, [isSearchOpen]);

  useEffect(() => () => document.body.classList.remove('search-overlay'), []);

  const showUSLogo = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK].includes(getLocale() as ELocale);
  const showMountainGetawaySummer = isOnSummerOverview && getLocale() === ELocale.EN_US;
  const showMountainNewsSummer = isOnSummerOverview
    && [ELocale.FR_FR, ELocale.DE_DE, ELocale.IT_IT, ELocale.EN_GB, ELocale.PL_PL].includes(getLocale() as ELocale);
  const showSkiGPT = [ELocale.EN_US, ELocale.EN_GB].includes(getLocale() as ELocale);

  const renderLogo = () => {
    if (showMountainGetawaySummer) return <SvgLogoMountainGetawaySummer />;
    if (showMountainNewsSummer) return <SvgLogoMountainNewsSummer />;
    return showUSLogo ? <SvgLogoSnow /> : <SvgLogoSki />;
  };

  useDefaultScrollToTop(0, 0);

  const refocusToRatingBlock = useCallback((e: FocusEvent) => {
    const relatedTarget = e?.relatedTarget as HTMLElement | null;
    if (relatedTarget?.id && relatedTarget.id.indexOf('onetrust') >= 0
      && !IS_SSR
      && hash === REVIEW_ANCHOR) {
      document.getElementById(REVIEW_ANCHOR)?.scrollIntoView();
    }
  }, [hash]);

  useEffect(() => {
    logoRef?.current?.addEventListener('focus', refocusToRatingBlock);
    return () => logoRef?.current?.removeEventListener('focus', refocusToRatingBlock);
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // App Router has no router.events; close search on every pathname change.
  useEffect(() => {
    closeSearch();
  }, [pathname, closeSearch]);

  const { noHeader } = useNoHeader();
  if (noHeader) {
    return null;
  }

  // The first two summerURLs entries become a sub-bar strip beneath
  // the main row (winterSwitch + summer.summer). The full set drives
  // the Stories hover-dropdown on desktop.
  const subBarLinks = summerURLs.slice(0, 2);

  return (
    <>
      {isSearchOpen && <SearchPopup closePopup={toggleSearch} />}

      {/* ─── Desktop chrome (md+) ────────────────────────────────────── */}
      <nav
        ref={divRef}
        className={cn('z-[12] h-[46px] max-md:hidden', palette.bg)}
      >
        <Container width="xl-only" className="flex h-[46px] items-center justify-between gap-x-4">
          <div className="flex h-[46px] items-center gap-x-6">
            <Link
              scroll={false}
              href={logoHrefUrl}
              prefetch={prefetch}
              ref={logoRef}
              tabIndex={-1}
              className="flex items-center [&_svg]:h-[26px]"
            >
              {renderLogo()}
            </Link>
            <div className="flex h-full items-center gap-x-1">
              <Link
                scroll={false}
                href={Routes.snowReports()}
                prefetch={prefetch}
                className={cn(
                  desktopLinkClass,
                  palette.hoverBg,
                  current === HeaderNavCurLink.SNOW_REPORTS && cn('!text-white', palette.currentBg),
                )}
              >
                {t('nav.global.snowWeather')}
              </Link>
              {isMainLocale() && (
                <Link
                  scroll={false}
                  href={Routes.tripPlanning()}
                  prefetch={prefetch}
                  className={cn(
                    desktopLinkClass,
                    palette.hoverBg,
                    current === HeaderNavCurLink.TRIP_PLANNING && cn('!text-white', palette.currentBg),
                  )}
                >
                  {t('nav.global.travel')}
                </Link>
              )}

              <div className="group relative h-full">
                <Link
                  href={Routes.magazine}
                  className={cn(
                    desktopLinkClass,
                    palette.hoverBg,
                    'gap-1',
                    current === HeaderNavCurLink.MAGAZINE && cn('!text-white', palette.currentBg),
                  )}
                >
                  {t('nav.global.summer.stories')}
                  <ChevronDown size={12} className="opacity-80" />
                </Link>

                {summerURLs.length > 0 && (
                  <div
                    className={cn(
                      'absolute right-0 top-full z-50 hidden min-w-[220px] flex-col py-2',
                      'rounded-md border border-border bg-white text-sm text-foreground',
                      'shadow-lg ring-1 ring-black/5',
                      'group-hover:flex group-focus-within:flex',
                    )}
                  >
                    {summerURLs.map((item, index) => (
                      <React.Fragment key={item.key}>
                        <Link
                          href={item.url}
                          className={cn(
                            'flex items-center gap-2 px-4 py-2 no-underline',
                            'text-foreground transition-colors hover:bg-muted hover:text-foreground',
                          )}
                        >
                          <span>{t(item.key as I18nKeyGenerated['common'])}</span>
                          {item.key.includes('summer.summer') && <SvgSunWhite className="h-3.5 w-3.5" />}
                        </Link>
                        {index === 1 && <hr className="my-1 border-border" />}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              {showSkiGPT && (
                <a
                  href="https://chat.onthesnow.com/"
                  className={cn(
                    desktopLinkClass,
                    'normal-case',
                    palette.hoverBg,
                    current === HeaderNavCurLink.SKIGPT && cn('!text-white', palette.currentBg),
                  )}
                >
                  SkiGPT
                </a>
              )}
            </div>
          </div>
          <div className="flex h-[46px] items-center gap-x-2">
            <HeaderSearch />
            <div className={cn('rounded bg-white/10', palette.hoverBg)}>
              <SimpleLangSelect isOnSummerOverview={isOnSummerOverview} />
            </div>
          </div>
        </Container>
      </nav>

      {/* ─── Mobile chrome (below md) ────────────────────────────────── */}
      <nav className={cn('z-[12] md:hidden', palette.bg)}>
        <div className="flex h-10 items-center justify-between gap-x-2 px-3">
          <Link
            scroll={false}
            href={logoHrefUrl}
            prefetch={prefetch}
            tabIndex={-1}
            className="flex items-center [&_svg]:h-[22px]"
          >
            {renderLogo()}
          </Link>
          <div className="flex h-10 flex-1 items-center justify-center">
            <Link
              scroll={false}
              href={Routes.snowReports()}
              prefetch={prefetch}
              className={cn(
                mobileLinkClass,
                current === HeaderNavCurLink.SNOW_REPORTS && palette.mobileActive,
              )}
            >
              {t('nav.global.snowWeather')}
            </Link>
            {isMainLocale() && (
              <Link
                scroll={false}
                href={Routes.tripPlanning()}
                prefetch={prefetch}
                className={cn(
                  mobileLinkClass,
                  current === HeaderNavCurLink.TRIP_PLANNING && palette.mobileActive,
                )}
              >
                {t('nav.global.travel')}
              </Link>
            )}
            <Link
              href={Routes.magazine}
              className={cn(
                mobileLinkClass,
                current === HeaderNavCurLink.MAGAZINE && palette.mobileActive,
              )}
            >
              {t('nav.global.summer.stories')}
            </Link>
            {showSkiGPT && (
              <a
                href="https://chat.onthesnow.com/"
                className={cn(
                  mobileLinkClass,
                  'normal-case',
                  current === HeaderNavCurLink.SKIGPT && palette.mobileActive,
                )}
              >
                SkiGPT
              </a>
            )}
          </div>
          <div
            tabIndex={-1}
            role="button"
            className={mobileIconButtonClass}
            onClick={toggleSearch}
          >
            <Search size={20} strokeWidth={2.25} />
          </div>
        </div>

        {/* Sub-bar: 2 quick summer URLs */}
        {subBarLinks.length > 0 && (
          <div className={cn('flex h-8 items-center justify-center gap-5 px-3', palette.subBarBg)}>
            {subBarLinks.map((item) => (
              <Link
                key={item.key}
                href={item.url}
                className={subBarLinkClass}
              >
                <span>{t(item.key as I18nKeyGenerated['common'])}</span>
                {item.key.includes('summer.summer') && <SvgSunWhite className="h-3.5 w-3.5" />}
              </Link>
            ))}
          </div>
        )}
      </nav>

    </>
  );
};
